/* eslint-disable react/no-unknown-property */
import React from 'react';
import { observer } from 'mobx-react-lite';
import { runInAction, toJS } from 'mobx';

import {
  CodeEditor,
  createKeymapExtension,
  insertNewlineAndIndent,
  indentMore,
  indentLess,
  type Extension,
} from '@pypestream/design-system';
import { ConfigPanel } from '../src/config-panel';
import { NodeColumns, TableRow, tableStore } from '../../../stores/table-store';
import { getColumnEditKind } from '../../../stores/utils/column-edit-kind';
import { getNodeColumnDisplayName } from '../../../stores/utils/column-display-names';
import { buildScriptFileNames } from '../../../stores/utils';
import { isJsonCapableColumn } from '../../../stores/utils/is-json-capable-column';
import {
  resolveInitialCharSeed,
  resolveSeedForNonce,
  type NonceSeed,
} from '../../../stores/utils/resolve-initial-char-seed';
import { useProStudioCtxSelector } from '../../../xstate/app.xstate';
import { getOrderedJson } from '../utils/get-ordered-json';
import { getAssetTypeList } from '../utils/get-asset-type-list';
import { NodeBehaviorsMultiSelect } from '../node-behaviors-multi-select';
import {
  commitAssetTypeChange,
  commitCommandChange,
  getCellErrorMessage,
  writeBehaviors,
} from '../src/cell';
import {
  cellErrorDescriptionId,
  resolveCellErrorAria,
} from '../src/cell-error-message';
import {
  getNodeTypeBadgeLetter,
  getNodeTypeBadgeModifier,
} from './node-type-badge';
import { autosizeTextarea } from './autosize-textarea';
import {
  buildPanelCellDraftUpdate,
  buildPanelCellCommitUpdate,
} from './panel-cell-updates';
import { buildJsonEditorKeyBindings } from './json-editor-keymap';
import { shouldCommitPanelEditorBlur } from './should-commit-panel-editor-blur';
import { composedContains } from './composed-contains';
import { isSessionForCell, type PanelEditSession } from './panel-edit-session';
import {
  performPanelCommit,
  type PanelCommitStore,
} from './perform-panel-commit';

import './expanded-cell-view-panel.scss';

/**
 * The RAW string the store currently holds for a cell — the value both the
 * session baseline and the commit read, so the two can never disagree about
 * whether anything changed (PSTUDIO-1433).
 */
const readPanelCellDraft = (rowId: string, columnId: string): string =>
  tableStore
    .cellDisplayValue(
      rowId,
      columnId as Parameters<typeof tableStore.cellDisplayValue>[1]
    )
    ?.toString() ?? '';

/**
 * `tableStore` seen through the narrow port `performPanelCommit` needs. Module
 * scope, not a hook: the store is a singleton, so there is nothing per-render
 * about it, and a stable object keeps the commit callback's deps empty.
 */
const panelCommitStore: PanelCommitStore = {
  isPanelEditRegistered: (rowId, columnId) =>
    tableStore.isPanelEditRegistered(rowId, columnId),
  endPanelEdit: () => tableStore.endPanelEdit(),
  readDraft: (rowId, columnId) => readPanelCellDraft(rowId, columnId),
  updateRow: (args) =>
    tableStore.updateRow(args as Parameters<typeof tableStore.updateRow>[0]),
  saveUnsavedChanges: () => tableStore.saveUnsavedChanges(),
  commitCellEdit: (args) =>
    tableStore.commitCellEdit(
      args as Parameters<typeof tableStore.commitCellEdit>[0]
    ),
  clearRowDirty: (rowId) => tableStore.clearRowDirty(rowId),
};

export const ExpandedCellViewPanel: React.FC<{
  viewOnly?: boolean;
}> = observer(({ viewOnly }) => {
  // PSTUDIO-3624: live-sync — while a cell is edited INLINE in the grid its
  // keystrokes live in a non-observable draft (perf, PSTUDIO-2821), so the
  // committed selectedCellValue() would be stale here. liveCellValueForPanel()
  // returns the observable live draft for the active inline-edit cell (else the
  // committed value), so the panel reflects typing as it happens.
  const valueToRender = tableStore.liveCellValueForPanel();
  const rowId = tableStore.selectedRowId();
  const columnId = tableStore.selectedCell()?.columnId;
  const isJsonColumn = tableStore.isJsonColumn(rowId, columnId);
  // PSTUDIO-1433: choose the JSON CodeEditor for a JSON-capable column — BUT for
  // richAssetContent also gate on the asset type. A scalar richAssetType
  // (Document / Video / Location / Datepicker / Secure …) stores a plain
  // string/URL, not JSON, so the JSON linter would permanently flag valid content
  // and the Enter contract would differ from the textarea. Mirror the store's own
  // value-aware isJsonColumn (isJsonAssetType) so the panel editor and the write
  // path agree. parameterInput / analyticsEvent have no asset type, so
  // isJsonCapableColumn alone is correct for them.
  // NOTE: WHICH richAssetType values count as JSON is a PRODUCT decision
  // (PSTUDIO-1433, Leo/Edan) and MAY be revisited/changed by the product manager
  // in future — keeping this gated on the store's isJsonColumn means both the
  // editor choice and the write path move together if that set changes.
  const useJsonEditor =
    isJsonCapableColumn(columnId) &&
    (columnId !== NodeColumns.richAssetContent || isJsonColumn);
  // PSTUDIO-1433: THE edit session — the single thing the commit path reads.
  //
  // Everything a commit needs is captured here when the edit begins, and the
  // commit works from this snapshot rather than from the current selection.
  // That distinction is load-bearing, because the panel's commit is not always
  // synchronous with what is selected: Enter commits and then moves the
  // selection down, so the incidental blur that follows arrives when `rowId` /
  // `columnId` (and the `before` snapshot derived from them) already describe
  // the NEXT cell. Committing off the live selection there wrote one cell's
  // undo baseline onto another row — Undo then restored a value that cell never
  // held. Anchoring to the session makes that blur a no-op instead: the session
  // is cleared by the commit that consumed it, so a second call has nothing to
  // save ("no double-save conflict", the ticket's own words).
  //
  // A ref, not state: it must be readable and clearable synchronously inside
  // one keystroke, and nothing renders from it.
  const editSessionRef = React.useRef<PanelEditSession | null>(null);
  // PSTUDIO-3624: ref to the auto-growing textarea (fallback editor) so it can
  // be re-sized to its content on mount and whenever the rendered value changes
  // (cell switch, undo/redo, external paste).
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  // PSTUDIO-1433: wrapper around the JSON CodeEditor, so an auto-open (entering
  // edit on a JSON-capable cell) can move focus INTO the editor.
  const jsonEditorWrapperRef = React.useRef<HTMLDivElement>(null);
  // PSTUDIO-1433 (type-to-edit seed): the store seed, consumed once per autofocus
  // nonce. Lives in a REF, not in the autofocus effect's closure, because React 18
  // replays an effect's cleanup+body (StrictMode on mount, and any remount), and
  // the consume-from-store step CLEARS the store — so a closure-local read loses
  // the character on the replayed run. `applied` makes the write one-shot too, so
  // a replay cannot record a second draft edit for one keystroke.
  const seedForNonceRef = React.useRef<NonceSeed | null>(null);
  // PSTUDIO-1433: the element the last pointer press landed on. A click on the
  // panel's own chrome (the field label, the node header) focuses nothing, so
  // the resulting blur carries a null `relatedTarget` and the focus target alone
  // cannot tell it apart from a click on the grid — yet the requirement is
  // explicit that clicking within the panel must not save. The pointerdown
  // origin settles it. Recorded document-wide in the capture phase (pointerdown
  // always precedes the blur it causes) and only interpreted at blur time,
  // against the panel resolved from the editor itself — so no global "which
  // panel is open" guesswork is needed.
  const lastPointerDownTargetRef = React.useRef<Node | null>(null);
  React.useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      // composedPath()[0] is the real target inside the CodeEditor's shadow root
      // (`e.target` is retargeted to the ps-code-editor host).
      lastPointerDownTargetRef.current =
        (e.composedPath?.()[0] as Node | undefined) ??
        (e.target as Node | null);
    };
    // PSTUDIO-1433: a KEYSTROKE invalidates the pointer context. The signal is
    // "the click that is blurring the editor landed inside the panel", so it is
    // only meaningful for the blur that click itself causes. A keyboard commit
    // (Enter blurs the editor programmatically, so `relatedTarget` is null too)
    // would otherwise inherit the stale pointerdown from whenever the user last
    // clicked INTO the editor — and be suppressed as if it were an in-panel
    // click. That silently dropped a committed edit; caught by the PSTUDIO-3882
    // pipe-character e2e in a full-suite run.
    const onKeyDown = () => {
      lastPointerDownTargetRef.current = null;
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  // Draft script files — the command editor's known-function set is built from
  // these + the standard functions, exactly like the grid's command cell.
  const { draftFiles } = useProStudioCtxSelector((ctx) => ({
    draftFiles: ctx.draft?.files ?? [],
  }));

  const canRender = tableStore.isSelectedCellRenderable();
  // PSTUDIO-3624: the selected cell is CLEARED for its node type (e.g. Message on
  // an Action node, Command on a Decision node) — the grid renders it greyed &
  // non-editable (nodeTypeDependentCells), so the panel hides its editor and
  // shows a "not applicable" note instead of a live control, keeping the grid and
  // panel enabled/disabled state in sync. A cleared cell can still be reached by
  // keyboard navigation (left as-is by product decision); this makes it
  // non-editable there too.
  const isClearedCell = tableStore.isSelectedCellClearedForNodeType();

  // PSTUDIO-3624: node header (shown above the panel body whenever a single
  // renderable cell is selected). It describes the NODE, not the cell — node
  // number + name on the left, node-type badge (D / A) on the right.
  const nodeNumber = rowId
    ? tableStore.getValue(rowId, NodeColumns.nodeNumber)
    : undefined;
  const nodeName = rowId
    ? tableStore.getValue(rowId, NodeColumns.nodeName)
    : undefined;
  const nodeTypeValue = rowId
    ? tableStore.getValue(rowId, NodeColumns.nodeType)
    : undefined;
  const nodeTypeBadge = getNodeTypeBadgeLetter(nodeTypeValue);
  // PSTUDIO-3624: color the badge per node type (Decision = baby blue, Action =
  // mint green) so it's recognizable at a glance (Mostafa's feedback). Built
  // once and reused by both badge instances (header + node-type editor).
  const nodeTypeBadgeClass = `c-expanded-cell-view__node-type-badge c-expanded-cell-view__node-type-badge--${getNodeTypeBadgeModifier(
    nodeTypeValue
  )}`;

  // PSTUDIO-3624: the panel body is a per-column editor — it must behave like
  // the selected table cell. `getColumnEditKind` is the same classifier the
  // table cell uses, so the panel and the grid agree on how a column is edited.
  // This block implements the kinds shown in the Figma design (number, and the
  // node-type select); every other kind falls back to the existing textarea
  // editor for now (follow-up block wires checkbox / behaviors / asset-type /
  // command with their store side-effects).
  const editKind = columnId
    ? getColumnEditKind(columnId as NodeColumns)
    : undefined;
  const columnLabel = getNodeColumnDisplayName(columnId as string | undefined);

  // PSTUDIO-3624: keep the fallback textarea sized to its content. Runs after
  // layout on mount and on every value / cell / edit-kind change; when another
  // editor kind is rendered the ref is null and this is a no-op.
  React.useLayoutEffect(() => {
    autosizeTextarea(textareaRef.current);
  }, [valueToRender, rowId, columnId, editKind]);

  // PSTUDIO-1433: when the panel is AUTO-opened by entering edit on a
  // JSON-capable cell, move focus into the panel's JSON editor so the user can
  // type there immediately (per product decision — the panel's editor, not the
  // grid inline editor, owns the edit). Driven by the store's one-shot
  // autofocus nonce (bumped in table.tsx's edit-entry reaction), so it fires on
  // every auto-open, including switching from one JSON cell to another while the
  // panel is already open. The CodeMirror editor inside ps-code-editor
  // initializes asynchronously, so poll a few animation frames for its
  // contenteditable (.cm-content) before focusing; the rAF is cancelled on
  // cleanup (perf rule: every scheduled callback has a matching teardown).
  const autofocusNonce = tableStore.expandedCellViewAutofocusNonce;
  React.useEffect(() => {
    if (autofocusNonce === 0) return undefined;
    // PSTUDIO-1433 (type-to-edit seed): the grid cell has no inline editor for a
    // JSON-capable column, so `startCellEditingWithChar` parks the character the
    // user typed in the store (addressed to this cell) and the panel applies it.
    // Consume it from the store ONCE PER AUTOFOCUS NONCE — into a ref, NOT into
    // this effect's closure. React 18 re-runs an effect after replaying its
    // cleanup (StrictMode double-invokes effects on mount; a remount does the
    // same), so a store read that also CLEARS the store is not idempotent: the
    // first run consumed the character and the second run — the one whose rAF
    // loop actually survives — found `null` and dropped it. Verified live: the
    // panel effect fired twice for a single keystroke under the same nonce, and
    // the typed character never reached the editor. The ref survives the replay
    // (the component instance is not recreated), so the second run re-reads the
    // SAME seed, while the store is still cleared on first read — which is what
    // keeps the seed one-shot per keystroke and stops a stale character from
    // surviving into a later auto-open.
    seedForNonceRef.current = resolveSeedForNonce(
      seedForNonceRef.current,
      autofocusNonce,
      () => {
        const fromStore = resolveInitialCharSeed({
          pendingChar: tableStore.pendingInitialChar,
          pendingCharCellId: tableStore.pendingInitialCharCellId,
          columnId,
          cellId: rowId && columnId ? `${rowId}-${columnId}` : null,
        });
        if (tableStore.pendingInitialCharCellId !== null) {
          runInAction(() => {
            tableStore.pendingInitialChar = null;
            tableStore.pendingInitialCharCellId = null;
          });
        }
        return fromStore;
      }
    );
    // Resolve the panel's active editor element to focus. For a JSON-capable
    // value it is the CodeMirror contenteditable, which lives inside the
    // ps-code-editor Web Component's (open) shadow root — pierce it explicitly
    // (a light-DOM querySelector('.cm-content') on the wrapper returns null).
    // For a scalar richAssetContent (asset type Video/Document/…) the panel
    // renders a plain <textarea> instead, so fall back to that — otherwise the
    // panel auto-opens for those assets but the caret is left on the grid <td>
    // (there is no inline editor to fall back to, since the JSON-capable cell
    // never mounts one). PSTUDIO-1433.
    const findEditor = (): HTMLElement | null => {
      const wrapper = jsonEditorWrapperRef.current;
      const cm = wrapper
        ? ((wrapper
            .querySelector('ps-code-editor')
            ?.shadowRoot?.querySelector('.cm-content') ??
            wrapper.querySelector('.cm-content')) as HTMLElement | null)
        : null;
      if (cm) return cm;
      return (textareaRef.current ?? null) as HTMLTextAreaElement | null;
    };
    // The deep active element, piercing open shadow roots (the .cm-content lives
    // in ps-code-editor's shadow root).
    const deepActive = (): Element | null => {
      let a: Element | null = document.activeElement;
      while (a?.shadowRoot?.activeElement) a = a.shadowRoot.activeElement;
      return a;
    };
    // PSTUDIO-1433: CodeMirror initializes asynchronously AND, right after the
    // panel opens, @uiw/react-codemirror can REPLACE its `.cm-content`
    // contenteditable node when the controlled `value` is (re)applied — which
    // silently drops the focus we just set (observed on the Enter edit-entry
    // path: cm.focus() lands, then the node is recreated and focus falls to
    // <body>). A single focus() therefore isn't enough. Poll animation frames
    // until the editor node exists, focus it, and KEEP re-focusing until the
    // focus has held for a few consecutive frames (settle), so a post-init node
    // swap can't leave the caret outside the panel. Bounded by a frame budget so
    // it can never spin, and cancelled on cleanup (perf rule: every scheduled
    // callback has a matching teardown).
    let frames = 0;
    let settled = 0;
    // PSTUDIO-1433: apply the typed character as the cell's NEW content (Sheets
    // semantics — typing over a selected cell replaces its value rather than
    // appending). Routed through the shared handleJsonEditorChange so the write
    // path is identical to real typing in the panel (raw draft now,
    // canonicalization deferred to commit-on-blur). Guarded to run at most once
    // per auto-open: the focus loop can call this from either exit branch.
    const applySeed = () => {
      const seed = seedForNonceRef.current;
      // The `applied` guard is on the REF (not a closure flag), so a replayed
      // effect run cannot write the same keystroke twice.
      if (seed == null || seed.applied || seed.char == null) return;
      seed.applied = true;
      handleJsonEditorChange(seed.char);
    };
    let rafId = requestAnimationFrame(function focusEditor() {
      const editor = findEditor();
      if (editor) {
        if (deepActive() === editor) {
          // Focus is currently inside the editor — count consecutive settled
          // frames; three in a row means the node has stopped swapping.
          if (++settled >= 3) {
            // ORDER IS LOAD-BEARING (PSTUDIO-3813): seed ONLY now. Focus entering
            // the editor already fired the wrapper's onFocus → takeFocusSnapshot,
            // so the undo baseline holds the cell's TRUE prior value; and because
            // the loop stops here, no further re-focus (hence no second snapshot)
            // can capture the seeded value. Seeding before the focus settles
            // would let a post-init CodeMirror node swap re-snapshot the typed
            // character and make Undo revert to it instead of the original.
            applySeed();
            return;
          }
        } else {
          // Not focused (first pass, or CM just recreated its node) — (re)focus.
          editor.focus();
          settled = 0;
        }
      }
      if (frames++ < 30) {
        rafId = requestAnimationFrame(focusEditor);
      } else if (findEditor()) {
        // Frame budget exhausted without the focus settling. An editor exists, so
        // its onFocus snapshot has run — seed anyway: losing the typed character
        // is worse than a caret that may need one click. With no editor at all
        // (panel closed / non-editable cell) the seed is simply dropped.
        applySeed();
      }
    });
    return () => cancelAnimationFrame(rafId);
    // Fire ONLY on a new autofocus request (the nonce) — a bare cell/edit-kind
    // change without a new auto-open never bumps the nonce, so it can't steal
    // focus into the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autofocusNonce]);

  // PSTUDIO-3624: the selected cell's validation error, read from the SAME
  // store index the grid cell uses — so the panel highlights the editor red
  // (and shows the message) identically to the errored cell in the table.
  const cellError =
    rowId && columnId
      ? getCellErrorMessage(rowId, columnId as string)
      : undefined;

  // The panel is the OTHER entrance to the same state, and it is the one a
  // keyboard user reaches for long values. The grid cell got `aria-invalid` /
  // `aria-describedby` in PSTUDIO-3990; without the same wiring here the panel
  // conveys the error by colour and position only (WCAG 1.4.1 / 3.3.1), and
  // the editor the user is focused on is not marked invalid at all.
  // The `'panel'` surface is load-bearing, not decoration: the grid renders its
  // own description node for this very cell (unconditionally, in the cell's
  // tooltip portal — `cell.tsx` `CellErrorTooltip`), so sharing one id would put
  // two elements with the same id in the document whenever the panel is open on
  // an errored cell, and `getElementById` would resolve both cells' and the
  // panel's `aria-describedby` to whichever came first in document order.
  const panelCellId = rowId && columnId ? `${rowId}-${columnId}` : '';
  const editorErrorAria = panelCellId
    ? resolveCellErrorAria(cellError, panelCellId, 'panel')
    : {};

  // Command select options (same source + shape as the grid's command cell):
  // uploaded scripts + dynamic native (system + library) functions, plus the
  // current value if it isn't one. Read actionNodeNames in the render body (not
  // inside the memo) so the MobX observer subscribes and re-renders when the list
  // loads/updates.
  const { actionNodeNames } = tableStore;
  const commandKnownFunctions = React.useMemo(
    () => new Set<string>(buildScriptFileNames(draftFiles, actionNodeNames)),
    [draftFiles, actionNodeNames]
  );
  const commandCurrent = typeof valueToRender === 'string' ? valueToRender : '';
  // Current stored asset type — used both as the select value and to render a
  // fallback option when it isn't one of the known asset types (mirrors grid).
  const assetTypeCurrent =
    typeof valueToRender === 'string' ? valueToRender : '';
  const commandOptions = React.useMemo(() => {
    const set = new Set(commandKnownFunctions);
    if (commandCurrent) set.add(commandCurrent);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [commandKnownFunctions, commandCurrent]);

  // PSTUDIO-1433: pretty-printed seed for the JSON CodeEditor.
  // Feed the LIVE draft value (valueToRender = liveCellValueForPanel) so the
  // panel stays in sync with the grid inline editor (grid↔panel live-sync).
  // When the value is already a string we pass it as-is (the editor emitted it);
  // when it's a parsed object (initial load / undo) we pretty-print it with the
  // stored order-map so key order is preserved (getOrderedJson, PSTUDIO-1433).
  const jsonSeedValue = React.useMemo(() => {
    if (!rowId || !columnId) return '';
    if (typeof valueToRender === 'string') return valueToRender;
    if (valueToRender == null) return '';
    return getOrderedJson({
      value: valueToRender as object,
      rowId,
      columnId: columnId as Parameters<typeof getOrderedJson>[0]['columnId'],
      stringify: true,
    });
  }, [valueToRender, rowId, columnId]);

  // PSTUDIO-1433: the commit itself, extracted from the blur handler so the
  // FOUR things that commit — blur out of the panel, Enter, closing the panel
  // with Shift+Enter, and the edit ending because the selection moved on — all
  // go through ONE path (DRY). Idempotent by construction: it CONSUMES the edit
  // session, so a second call in the same keystroke has nothing left to save
  // (the ticket's "no double-save conflict" requirement — Enter commits and then
  // moves the selection, which blurs the editor in the same tick).
  // `keepEditing` is for the one caller whose focus does NOT leave the editor
  // (Enter on the last visible row, where the selection has nowhere to move):
  // the flag must stay set, or the grid's type-to-edit claims the next
  // keystroke and replaces the whole cell — see commitAndMoveDown.
  const commitPanelEditorValue = React.useCallback(
    (options?: { keepEditing?: boolean }) => {
      const session = editSessionRef.current;
      // Consume the session FIRST: everything below targets the snapshot, and a
      // re-entrant commit (the blur that Enter's own selection move triggers)
      // must find nothing left to do.
      editSessionRef.current = null;
      // The SEQUENCE lives in `performPanelCommit` — which cell, which value,
      // which undo baseline, and what happens when the session was torn down
      // mid-edit. Keeping it out of the component is what lets a test pin the
      // order against a fake store instead of re-implementing it (mutating this
      // path used to leave the whole suite green).
      performPanelCommit(panelCommitStore, session, buildPanelCellCommitUpdate);

      // Clear the editing flag synchronously (mirrors inline editor
      // _clearCellEditingState).
      runInAction(() => {
        tableStore.isCellBeingEdited = options?.keepEditing === true;
      });
    },
    []
  );

  // PSTUDIO-1433: the SECOND CHANCE for a suppressed commit.
  //
  // Clicking the panel's own chrome deliberately does NOT save (the ticket's
  // "clicking within or around the panel should not trigger a save"), which
  // leaves a real edit sitting in the draft with no pending blur to commit it.
  // Whatever ends that edit next — selecting another cell, closing the panel,
  // navigating away — has to flush it, or the change never reaches the
  // database and the user loses it on the next reload. This cleanup runs on
  // exactly those transitions, and is a no-op whenever a normal commit already
  // consumed the session.
  // Tracks whether the panel is STILL mounted a microtask after a teardown.
  // React 18 StrictMode (on unconditionally via `main.tsx`) runs
  // setup → cleanup → setup on mount, so a cleanup-only effect fires
  // milliseconds after the panel appears — and the panel auto-opens exactly
  // when a JSON cell ENTERS edit, i.e. when `isCellBeingEdited` is true. The
  // replay would clear it, killing every grid shortcut gated on that flag. Dev
  // only, and invisible to the e2e suite, which runs a production build (same
  // class as the type-to-edit seed above). No deps: it must re-arm on every
  // render, so "still mounted" is answered for the CURRENT mount.
  const panelMountedRef = React.useRef(false);
  React.useEffect(() => {
    panelMountedRef.current = true;
    return () => {
      panelMountedRef.current = false;
    };
  });
  React.useEffect(
    () => () => {
      // A pending session is flushed SYNCHRONOUSLY: this is also the
      // cell-switch path, where the panel legitimately stays mounted and the
      // old cell's edit still has to land.
      if (editSessionRef.current) {
        commitPanelEditorValue();
        return;
      }
      // No session left to flush — but the editing flag may still be RAISED,
      // because Enter with nowhere to move deliberately keeps it set while the
      // caret stays in the editor. Removing a focused node fires no `blur` in
      // any browser, so an editor unmounted from under that state (row deleted,
      // asset type changed by a coworker, selection cleared by "Go to node")
      // used to leave `isCellBeingEdited` true forever — with it, every grid
      // shortcut gated on that flag stays dead until the next full edit.
      //
      // Deferred by one microtask so a StrictMode replay can be told apart from
      // a real unmount: the replay re-runs setup immediately after this
      // cleanup, so `panelMountedRef` is true again by the time this runs.
      queueMicrotask(() => {
        if (panelMountedRef.current) return;
        if (tableStore.isCellBeingEdited) {
          runInAction(() => {
            tableStore.isCellBeingEdited = false;
          });
        }
        tableStore.endPanelEdit();
      });
    },
    [rowId, columnId, commitPanelEditorValue]
  );

  // PSTUDIO-1433: shared blur handler — used by BOTH the textarea and the JSON
  // editor wrapper so the commit decision stays DRY (single source of truth).
  //
  // The decision itself is the pure `shouldCommitPanelEditorBlur`. It replaces
  // the earlier "is the next focus still inside the editor WRAPPER" guard, which
  // was too narrow: a click on the panel's own field label focuses nothing, so
  // `relatedTarget` was null and the click sailed through and saved — the exact
  // failure the 25 Aug manual test measured against the ticket's "clicking
  // within or around the panel should not trigger a save".
  const handleEditorBlur = React.useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      const next = e.relatedTarget as Node | null;
      // Resolve the panel from the editor that is blurring, rather than by
      // querying for whichever side panel happens to be open.
      const panelEl = e.currentTarget.closest('.c-config-panel');
      // composedContains, not Node.contains: the pointerdown target recorded
      // above is `composedPath()[0]`, which for the JSON editor is a node
      // INSIDE ps-code-editor's shadow root. Native contains() is blind across
      // that boundary, so a click on the editor's own gutter/scroller was read
      // as a click on the grid and saved — the ticket forbids exactly that.
      const contains = (node: Node | null) => composedContains(panelEl, node);

      if (
        !shouldCommitPanelEditorBlur({
          hasNextFocus: !!next,
          nextFocusInsidePanel: contains(next),
          pointerDownInsidePanel: contains(lastPointerDownTargetRef.current),
        })
      ) {
        // Focus left the editor but stayed in the panel's world: do NOT save
        // (ticket requirement). The SESSION deliberately survives, so returning
        // to the editor resumes the same edit and the eventual commit still
        // sees the real change — and if the user never returns, the
        // cell-change / unmount effect above flushes it, so a suppressed commit
        // delays the write but never loses it.
        //
        // The EDITING FLAG is a different thing and must still be cleared: no
        // editor holds focus any more. `isCellBeingEdited` gates grid shortcuts
        // (Shift+Enter's panel toggle is `!isEditing`), so leaving it set here
        // left the whole grid's shortcut layer dead after a click on the panel's
        // chrome — caught by the PSTUDIO-3624 panel e2e in a full-suite run.
        runInAction(() => {
          tableStore.isCellBeingEdited = false;
        });
        return;
      }

      commitPanelEditorValue();
    },
    [commitPanelEditorValue]
  );

  // PSTUDIO-1433: Enter in the JSON editor commits and moves the selection one
  // visible row down — the same choreography the panel textarea and the grid
  // already use (commitAndMoveVertically), so committing behaves identically
  // wherever you are editing.
  const commitAndMoveDown = React.useCallback(() => {
    // Resolve the target from the SESSION (the cell being edited), falling back
    // to the selection only when there is no open session — the same anchoring
    // the commit itself uses, so the move can never address a different cell
    // than the save did.
    const session = editSessionRef.current;
    const targetRowId = session?.rowId ?? rowId;
    const targetColumnId = session?.columnId ?? columnId;

    // KEEP the editing flag raised, unconditionally. The caret is still in this
    // editor at this instant, and it stays there until the selection move
    // actually lands — `scrollToAndFocusCell` defers through `setTimeout` + two
    // animation frames. Clearing the flag here (even "only when we are about to
    // move") opened a window of tens of milliseconds where focus was in the
    // editor while the grid believed nothing was being edited: its capture-phase
    // type-to-edit then claimed the next character and replaced the whole cell
    // with it. Predicting the move beforehand only narrowed that window to every
    // row but the last.
    //
    // The flag is released by whatever ends the edit for real: the blur that the
    // landing focus causes (the session is already consumed, so that commit is a
    // no-op that only clears the flag), or — when the editor is torn down with
    // no blur at all — the panel's teardown effect.
    commitPanelEditorValue({ keepEditing: true });

    if (targetRowId && targetColumnId) {
      tableStore.commitAndMoveVertically(
        targetRowId,
        targetColumnId as NodeColumns,
        'down'
      );
    }
  }, [commitPanelEditorValue, rowId, columnId]);

  // PSTUDIO-1433: Shift+Enter opens/closes the Expanded Cell View. From inside
  // the editor the grid's own Shift+Enter toggle is unreachable — focus here
  // sets `isCellBeingEdited`, which `shouldToggleExpandedCellView` deliberately
  // excludes — so the editor drives the same store action itself, committing
  // first so closing the panel never discards the edit.
  const commitAndTogglePanel = React.useCallback(() => {
    commitPanelEditorValue();
    // The focus hand-back that closing needs lives in `setExpandedCellViewOpen`
    // — one owner for every close entrance (PSTUDIO-1433).
    tableStore.toggleExpandedCellViewOpen();
  }, [commitPanelEditorValue]);

  // The key bindings are built ONCE and read the current handlers through a ref.
  // @uiw/react-codemirror compares its `extensions` prop by reference: rebuilding
  // the array when the selected cell changes would re-run CodeMirror's
  // reconfigure effect for nothing.
  const jsonKeyHandlersRef = React.useRef({
    commitAndMoveDown,
    commitAndTogglePanel,
  });
  React.useEffect(() => {
    jsonKeyHandlersRef.current = { commitAndMoveDown, commitAndTogglePanel };
  }, [commitAndMoveDown, commitAndTogglePanel]);
  const jsonEditorExtensions = React.useMemo<Extension[]>(
    () => [
      createKeymapExtension(
        buildJsonEditorKeyBindings({
          onCommit: () => jsonKeyHandlersRef.current.commitAndMoveDown(),
          onTogglePanel: () =>
            jsonKeyHandlersRef.current.commitAndTogglePanel(),
          insertNewline: insertNewlineAndIndent,
          indentMore,
          indentLess,
        })
      ),
    ],
    []
  );

  // PSTUDIO-1433 (DRY — mirrors the shared handleEditorBlur): capture the committed
  // value + JSON map sibling at focus so an undo/redo command can restore it on
  // blur (PSTUDIO-2154). BOTH the textarea onFocus and the JSON wrapper onFocus call
  // this one helper, so the snapshot shape lives in a single place.
  const takeFocusSnapshot = React.useCallback(() => {
    runInAction(() => {
      tableStore.isCellBeingEdited = true;
      // PSTUDIO-1433: one baseline per EDIT SESSION, not per focus. Focus can
      // leave and come back within the same uncommitted edit — clicking the
      // panel's own chrome and then back into the editor is the ordinary case,
      // and it must not save (ticket requirement). Re-snapshotting on that
      // return would re-baseline against the DRAFT, so the eventual commit would
      // see "nothing changed" and silently drop the user's edit.
      // …but ONLY while the store still registers it, and only while the
      // editor kind it was opened for still applies. Undo/redo
      // (`cancelCellEditing`), a delete, a coworker merge and a version reload
      // all call `endPanelEdit()` on their own, and the ref knows nothing about
      // that: resuming a de-registered session means the eventual commit finds
      // `stillRegistered === false` and writes NOTHING — the user's edit is
      // gone at the next reload, silently. Falling through instead opens a
      // FRESH session: re-registered, and re-baselined against the value that
      // is actually there now. `isJsonColumn` is part of the identity because
      // for `richAssetContent` it depends on the asset TYPE, which an external
      // write can flip mid-session — a stale flag writes (or drops) the JSON
      // key-order sibling.
      const openSession = editSessionRef.current;
      if (
        openSession &&
        isSessionForCell(openSession, rowId, columnId) &&
        openSession.isJsonColumn === isJsonColumn &&
        tableStore.isPanelEditRegistered(
          openSession.rowId,
          openSession.columnId
        )
      ) {
        return;
      }
      if (!rowId || !columnId) {
        editSessionRef.current = null;
        return;
      }
      // PSTUDIO-1433: make the session visible to the store. Every cross-cutting
      // guard (coworker merge, subscription reload, cross-tab reload,
      // hasActiveCellEdit) asks the store which cell is being edited, and the
      // panel sets none of the inline editor's fields — so without this an
      // external write could land on the very cell the user is typing in.
      tableStore.beginPanelEdit(rowId, columnId);
      editSessionRef.current = {
        rowId,
        columnId,
        // Baseline read through the SAME reader the commit compares against —
        // a baseline taken from a different projection of the value makes
        // "changed or not" answerable two different ways.
        baseline: readPanelCellDraft(rowId, columnId),
        isJsonColumn,
        before: {
          [columnId]: toJS(tableStore.getValue(rowId, columnId)),
          ...(isJsonColumn && {
            [`${columnId}Map`]: toJS(
              tableStore.getValue(
                rowId,
                `${columnId}Map` as Parameters<typeof tableStore.getValue>[1]
              )
            ),
          }),
        },
      };
    });
  }, [rowId, columnId, isJsonColumn]);

  // PSTUDIO-1433: onChange for the JSON CodeEditor — mirrors the textarea onChange.
  // Reuses buildPanelCellDraftUpdate + isJsonColumn (the same value-dependent flag
  // the textarea uses) so the write path is identical (DRY): keystrokes write the
  // RAW draft; canonicalization is deferred to commit-on-blur (handleEditorBlur).
  const handleJsonEditorChange = React.useCallback(
    (value: string) => {
      if (!rowId || !columnId) return;
      // PSTUDIO-1433: a draft write with no open session would be uncommittable
      // — the commit path reads the session and would find nothing to save.
      // Normally focus opened it, but two paths write without a fresh focus:
      // the type-to-edit seed (applied programmatically) and typing that
      // continues after an Enter commit whose selection had nowhere to move.
      // takeFocusSnapshot is idempotent for the same cell, so calling it here
      // is free when a session already exists and correct when it does not.
      takeFocusSnapshot();
      runInAction(() => {
        tableStore.isCellBeingEdited = true;
        tableStore.updateRow({
          rowId,
          updates: buildPanelCellDraftUpdate(columnId, value, isJsonColumn),
          skipValidationRules: true,
          saveImmediately: false,
        });
        tableStore.markRowDirty(rowId);
      });
    },
    [rowId, columnId, isJsonColumn, takeFocusSnapshot]
  );

  // PSTUDIO-1433: onFocus for the JSON editor wrapper — snapshots only on the FIRST
  // focus into the wrapper (relatedTarget guard: moving focus between the editor's
  // own internals is NOT a new edit and must not clobber the atomic-undo baseline).
  // The snapshot itself is delegated to the shared takeFocusSnapshot.
  const handleJsonEditorFocus = React.useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const wrapper = e.currentTarget;
      const prev = e.relatedTarget as Node | null;
      if (prev && wrapper.contains(prev)) return;
      takeFocusSnapshot();
    },
    [takeFocusSnapshot]
  );

  // <<< MOVED VERBATIM FROM controls-bar.tsx handleKeyDown (lines 154-229).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key !== 'Enter') return;

    if (e.metaKey || e.ctrlKey) {
      // Cmd/Ctrl + Enter: insert a line break at the cursor position
      e.preventDefault();

      if (!rowId || !columnId) return;

      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = textarea.value;
      const newValue =
        currentValue.substring(0, start) + '\n' + currentValue.substring(end);

      // Same session guarantee as the JSON editor's onChange — a draft write
      // always happens inside an edit session, whichever editor made it.
      takeFocusSnapshot();
      runInAction(() => {
        tableStore.isCellBeingEdited = true;

        tableStore.updateRow({
          rowId,
          updates: buildPanelCellDraftUpdate(columnId, newValue, isJsonColumn),
          skipValidationRules: true,
          saveImmediately: false,
        });
        tableStore.markRowDirty(rowId);
      });

      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 1;
      });
    } else {
      // Plain Enter: submit changes and navigate to the cell below
      e.preventDefault();

      const currentRowId = rowId;
      const currentColumnId = columnId;

      // Blur triggers onBlur which saves unsaved changes
      e.currentTarget.blur();

      if (!currentRowId || !currentColumnId) return;

      // PSTUDIO-1433: same edge as the JSON editor's Enter. On the last visible
      // row the selection has nowhere to go, so `commitAndMoveVertically` is a
      // no-op and focus is left on <body> with the cell still selected — the
      // grid's capture-phase type-to-edit then eats the next character. Keep the
      // editing flag raised so that path stays closed; whatever ends the edit
      // for real (the landing focus's blur, or the panel's teardown effect)
      // releases it.
      if (!tableStore.getVerticalNeighborRow(currentRowId, 'down')) {
        runInAction(() => {
          tableStore.isCellBeingEdited = true;
        });
      }

      // PSTUDIO-2821: move selection to the same column one VISIBLE row below
      // (Enter) or above (Shift+Enter). Shares the single store implementation
      // with the inline-cell Enter handler (DRY) — the commit itself already
      // happened via the blur() above; this only moves focus/selection.
      tableStore.commitAndMoveVertically(
        currentRowId,
        currentColumnId as NodeColumns,
        e.shiftKey ? 'up' : 'down'
      );
    }
  };
  // >>> END MOVED BLOCK

  return (
    <ConfigPanel>
      {canRender && (
        // PSTUDIO-3624: node header — node number + name (left), type badge
        // (right). Figma type spec: number = Subtitle 3 (Gordita Medium 12px/
        // 20px), name = Body 3 (Gordita Regular 12px/20px).
        <div
          className="c-expanded-cell-view__node-header"
          test-id="ps-expanded-cell-view-node-header"
        >
          <div className="c-expanded-cell-view__node-header-title">
            <span className="c-expanded-cell-view__node-number">
              {nodeNumber != null ? String(nodeNumber) : ''}
            </span>
            <span className="c-expanded-cell-view__node-name">
              {nodeName != null ? String(nodeName) : ''}
            </span>
          </div>
          <span
            className={nodeTypeBadgeClass}
            test-id="ps-expanded-cell-view-node-type-badge"
          >
            {nodeTypeBadge}
          </span>
        </div>
      )}
      {canRender ? (
        isClearedCell ? (
          // PSTUDIO-3624: CLEARED cell for this node type — hide the editor and
          // show a short "not applicable" note. Mirrors the grid, which paints
          // this cell as an empty greyed data-disabled <td>. The column label is
          // kept so the user still sees which field it is.
          <div
            className="c-expanded-cell-view__body c-expanded-cell-view__body--cleared"
            test-id="ps-expanded-cell-view-cleared"
          >
            <div
              className="c-expanded-cell-view__field-label"
              test-id="ps-expanded-cell-view-field-label"
            >
              {columnLabel}
            </div>
            <span
              className="c-expanded-cell-view__value-readonly"
              test-id="ps-expanded-cell-view-cleared-note"
            >
              This field does not apply to{' '}
              {nodeTypeBadge === 'D' ? 'a Decision' : 'an Action'} node.
            </span>
          </div>
        ) : (
          // PSTUDIO-3624: focus ENTERING the panel means the user left the grid's
          // inline editor. That inline edit is deliberately left OPEN when focus
          // moves to a side panel — an incidental blur keeps the cell open
          // (PSTUDIO-2821) — so editingRowId/editingColumnId still point at the
          // selected cell and liveCellDraft lingers. liveCellValueForPanel() then
          // keeps returning the stale live draft, freezing this controlled editor:
          // every keystroke reverts, so the panel looks un-editable until you click
          // back into the grid. Commit the open inline edit here — the draft is
          // materialized into the row and the editing state cleared, so the panel
          // switches to the committed value it can actually edit. commitCellEditing
          // is idempotent (a no-op when nothing is being edited), and the capture
          // phase runs before the focused field's own onFocus. Covered by
          // resolvePanelCellValue unit tests + the e2e stale-lock test.
          <div
            className="c-expanded-cell-view__body"
            test-id="ps-expanded-cell-view-body"
            onFocusCapture={() => {
              // PSTUDIO-3624: focus entering the panel means the user is now
              // editing here, so commit the grid's open inline edit (materialize
              // the draft + clear editingRowId/editingColumnId). This is also what
              // makes the auto-open path correct (PSTUDIO-1433): the grid's
              // capture-phase Enter / Esc / type-to-edit handlers are gated on
              // editingColumnId != null, so clearing it here hands those keystrokes
              // to the panel's own editor (Enter inserts a JSON newline, Esc closes
              // the panel) instead of committing + moving the grid selection. The
              // panel does NOT collapse on this commit because the cell stays
              // selected (handleBlur in cell.tsx skips deselect when focus enters
              // the panel), and the panel renders off selectedCell(), not
              // editingColumnId. commitCellEditing is idempotent (no-op when
              // nothing is being edited). Covered by resolvePanelCellValue unit
              // tests + the e2e stale-lock test.
              tableStore.commitCellEditing();
            }}
          >
            {/* Column label (uppercased in CSS), e.g. "NODE #", "NODE TYPE". */}
            <div
              className="c-expanded-cell-view__field-label"
              test-id="ps-expanded-cell-view-field-label"
            >
              {columnLabel}
            </div>
            <div
              className={`c-expanded-cell-view__field${
                cellError ? ' c-expanded-cell-view__field--error' : ''
              }`}
              test-id="ps-expanded-cell-view-field"
            >
              {/* PSTUDIO-3624: the validation error renders ABOVE the editor, not
                below it. For a cell with a lot of data the auto-growing editor
                pushed the message far down the scroll where it was easy to miss
                (Slack feedback: "less obvious when it's at the bottom"). Placing
                it first — and pinning it sticky to the top of the scrolling
                panel body (see .c-expanded-cell-view__field-error SCSS) — keeps
                it visible while the long value scrolls underneath. */}
              {cellError && (
                <div
                  className="c-expanded-cell-view__field-error"
                  test-id="ps-expanded-cell-view-field-error"
                  id={
                    panelCellId
                      ? cellErrorDescriptionId(panelCellId, 'panel')
                      : undefined
                  }
                >
                  {cellError}
                </div>
              )}
              {editKind === 'readonly' ? (
                <span
                  className="c-expanded-cell-view__value-readonly"
                  test-id="ps-expanded-cell-view-value-readonly"
                >
                  {valueToRender != null ? String(valueToRender) : ''}
                </span>
              ) : editKind === 'checkbox' ? (
                // Boolean cell (answerRequired / nluDisabled) — write-through via the
                // table's writeCellValue; respect rule-disabled state (isCellDisabled).
                <input
                  type="checkbox"
                  className="c-expanded-cell-view__value-checkbox"
                  test-id="ps-expanded-cell-view-value-checkbox"
                  checked={valueToRender === true}
                  disabled={
                    viewOnly ||
                    (!!rowId &&
                      !!columnId &&
                      tableStore.isCellDisabled(
                        rowId,
                        columnId as keyof TableRow
                      ))
                  }
                  onChange={(e) => {
                    if (!rowId || !columnId) return;
                    tableStore.writeCellValue(
                      rowId,
                      columnId,
                      e.target.checked
                    );
                  }}
                />
              ) : editKind === 'behaviors' && rowId ? (
                // Behaviors multi-select — the SAME table component, committing
                // through the shared writeBehaviors so the nluDisabled side-effect
                // stays in sync with the grid.
                <NodeBehaviorsMultiSelect
                  rowId={rowId}
                  columnId={columnId}
                  onEdit={(values) => writeBehaviors(rowId, values)}
                />
              ) : editKind === 'select' && columnId === NodeColumns.nodeType ? (
                // Node-type editor — the SAME store path as the table cell
                // (recordRowEdit → updateRowType clears type-dependent fields as one
                // undo/redo command), so changing the type here matches the grid.
                <div className="c-expanded-cell-view__node-type-select">
                  <span className={nodeTypeBadgeClass}>{nodeTypeBadge}</span>
                  <select
                    test-id="ps-expanded-cell-view-node-type-select"
                    className="c-expanded-cell-view__node-type-select-control"
                    value={nodeTypeBadge === 'D' ? 'd' : 'a'}
                    disabled={viewOnly}
                    onChange={(e) => {
                      if (!rowId) return;
                      const next = e.target.value as TableRow['nodeType'];
                      tableStore.recordRowEdit(
                        rowId,
                        'Change node type',
                        () => {
                          tableStore.updateRowType({ rowId, newRowType: next });
                          tableStore.clearCellValidationError(
                            rowId,
                            NodeColumns.nodeType
                          );
                        },
                        NodeColumns.nodeType
                      );
                    }}
                  >
                    <option value="d">Decision Node</option>
                    <option value="a">Action Node</option>
                  </select>
                </div>
              ) : editKind === 'select' &&
                columnId === NodeColumns.richAssetType ? (
                // Asset-type editor — commits via the shared commitAssetTypeChange
                // (auto-adjusts behaviors/nluDisabled + seeds default content), so it
                // behaves exactly like the grid's asset-type cell.
                <select
                  test-id="ps-expanded-cell-view-asset-type-select"
                  className="c-expanded-cell-view__value-select"
                  value={assetTypeCurrent}
                  disabled={viewOnly}
                  onChange={(e) => {
                    if (rowId) commitAssetTypeChange(rowId, e.target.value);
                  }}
                >
                  {/* Mirror the grid cell: if the stored asset type is not in the
                    known list (removed / legacy), keep it as a current-value
                    option so the controlled <select> isn't rendered empty and a
                    stray onChange can't silently overwrite it (see cell.tsx). */}
                  {assetTypeCurrent &&
                    !getAssetTypeList().some(
                      ({ type }) => type === assetTypeCurrent
                    ) && (
                      <option value={assetTypeCurrent}>
                        {assetTypeCurrent}
                      </option>
                    )}
                  {getAssetTypeList().map(({ type, label }) => (
                    <option key={type} value={type}>
                      {label}
                    </option>
                  ))}
                </select>
              ) : editKind === 'select' && columnId === NodeColumns.command ? (
                // Command editor — commits via the shared commitCommandChange (only a
                // known function name is stored, else cleared), identical to the grid.
                <select
                  test-id="ps-expanded-cell-view-command-select"
                  className="c-expanded-cell-view__value-select"
                  value={commandCurrent}
                  disabled={viewOnly}
                  onChange={(e) => {
                    if (rowId)
                      commitCommandChange(
                        rowId,
                        e.target.value,
                        commandKnownFunctions
                      );
                  }}
                >
                  {!commandCurrent && <option value="">—</option>}
                  {commandOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              ) : editKind === 'number' ? (
                // Number editor (e.g. Node #) — commits via the table's own
                // writeCellValue path (records undo + saves), keyed so an external
                // change (paste/undo) re-mounts the uncontrolled input.
                <input
                  type="number"
                  className="c-expanded-cell-view__value-input"
                  test-id="ps-expanded-cell-view-value-input"
                  key={`${rowId}:${columnId}:${
                    nodeNumber != null ? String(nodeNumber) : ''
                  }`}
                  defaultValue={nodeNumber != null ? String(nodeNumber) : ''}
                  disabled={viewOnly || !tableStore.isSelectedCellEditable()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // Commit (onBlur → writeCellValue) then move the selection the
                      // same way the textarea's Enter does, so intra-panel commit
                      // behavior is consistent across editors.
                      e.currentTarget.blur();
                      if (rowId && columnId) {
                        tableStore.commitAndMoveVertically(
                          rowId,
                          columnId as NodeColumns,
                          e.shiftKey ? 'up' : 'down'
                        );
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (!rowId || !columnId) return;
                    const raw = e.currentTarget.value;
                    tableStore.writeCellValue(
                      rowId,
                      columnId,
                      raw === '' ? undefined : Number(raw)
                    );
                  }}
                />
              ) : useJsonEditor ? (
                // PSTUDIO-1433: JSON-capable columns (parameterInput / analyticsEvent /
                // richAssetContent) get a CodeMirror JSON editor with syntax highlighting
                // and a non-blocking linter. The editor is CONTROLLED with the live draft
                // value (jsonSeedValue = liveCellValueForPanel → pretty-printed) so the
                // grid↔panel live-sync stays intact; @lit/react's per-render prop
                // re-assert is a no-op because the re-applied value equals what the editor
                // just emitted (Lit !== dirty-check). See memory
                // reference_lit_react_controlled_value_fights_wc_drag.
                <div
                  ref={jsonEditorWrapperRef}
                  className={`c-expanded-cell-view__json-editor${
                    viewOnly || tableStore.isSelectedCellEditable() === false
                      ? ' c-expanded-cell-view__json-editor--readonly'
                      : ''
                  }`}
                  test-id="ps-expanded-cell-view-json-editor"
                  // Single commit point on wrapper blur → one atomic undo (PSTUDIO-2154).
                  onFocus={handleJsonEditorFocus}
                  onBlur={handleEditorBlur}
                >
                  <CodeEditor
                    theme="githubLight"
                    // `language` IS part of the generated wrapper's props: the DS
                    // types flow dist/index.d.ts → src/components → code-editor.d.ts
                    // → `GenerateComponentTypeWithChildren<CodeEditorWC>`, and
                    // code-editor.wc.d.ts declares `language?: 'json'`. An earlier
                    // `@ts-expect-error` here was dead (TS2578 under `tsc -b
                    // tsconfig.app.json --force`) and its note was wrong: the
                    // "Lint and Build pro-studio" job cannot catch either error,
                    // because the package's `build` runs bare `tsc` against a
                    // solution-style tsconfig (`files: []`, references only), which
                    // type-checks NOTHING (verified: exit 0, no output).
                    // If this line ever reports TS2322 locally, the design-system
                    // `dist/` is STALE — rebuild it (this PR changes the WC), do not
                    // re-add a suppression.
                    language="json"
                    // PSTUDIO-1433: the product key contract for this editor —
                    // Enter commits, Cmd/Ctrl+Enter inserts a line, Shift+Enter
                    // toggles the panel, Tab indents (bound explicitly in
                    // buildJsonEditorKeyBindings, on top of the default
                    // `indentWithTab`). Given top precedence so it beats
                    // CodeMirror's own Enter.
                    extensions={jsonEditorExtensions}
                    value={jsonSeedValue}
                    disabled={!tableStore.isSelectedCellEditable() || viewOnly}
                    onChange={handleJsonEditorChange}
                  />
                </div>
              ) : (
                <textarea
                  ref={textareaRef}
                  className="c-expanded-cell-view__textarea"
                  test-id="ps-expanded-cell-view-textarea"
                  {...editorErrorAria}
                  onKeyDown={handleKeyDown}
                  // PSTUDIO-1433: shared focus-snapshot (DRY — same helper the JSON
                  // wrapper uses); a plain textarea has no focusable descendants, so
                  // it never needs the JSON wrapper's relatedTarget guard.
                  onFocus={takeFocusSnapshot}
                  // <<< MOVED VERBATIM FROM controls-bar.tsx textarea onChange.
                  onChange={(e) => {
                    // Every draft write opens (or reuses) the edit session the
                    // commit path reads — see handleJsonEditorChange.
                    takeFocusSnapshot();
                    runInAction(() => {
                      tableStore.isCellBeingEdited = true;

                      if (!rowId) return;
                      if (!columnId) return;

                      // Update MobX store immediately (but don't save to DB).
                      // PSTUDIO-3882: write the RAW typed string (draft) — do
                      // NOT canonicalize array columns per keystroke, or a
                      // trailing `|` (the whatsNext separator) is split out
                      // mid-typing and can never be typed. Canonicalization is
                      // deferred to commit-on-blur (buildPanelCellCommitUpdate).
                      tableStore.updateRow({
                        rowId,
                        updates: buildPanelCellDraftUpdate(
                          columnId,
                          e.currentTarget.value,
                          isJsonColumn
                        ),
                        skipValidationRules: true,
                        saveImmediately: false,
                      });

                      // Track row as dirty
                      tableStore.markRowDirty(rowId);
                    });
                  }}
                  // >>> END MOVED BLOCK
                  // PSTUDIO-3624 + PSTUDIO-1433 + PSTUDIO-3882: reuse the shared
                  // handleEditorBlur so both editors (textarea and JSON CodeEditor)
                  // commit through one path (DRY) — including the commit-on-blur
                  // canonicalization for array/JSON columns.
                  onBlur={handleEditorBlur}
                  style={{
                    backgroundColor:
                      tableStore.isSelectedCellEditable() === false
                        ? 'rgb(243, 243, 243)'
                        : 'transparent',
                    opacity: !tableStore.isSelectedCellEditable() ? 0.5 : 1,
                  }}
                  disabled={!tableStore.isSelectedCellEditable() || viewOnly}
                  value={valueToRender || ''}
                />
              )}
            </div>
            {/* PSTUDIO-3624: the "updated by" (author + timestamp) meta row is
              HIDDEN. The data model has no updatedBy/updatedAt for a node/cell and
              the API returns neither the author nor the timestamp, so rendering a
              hardcoded "—" placeholder would only mislead users into thinking the
              feature exists. Per team feedback (Slack thread), it is hidden rather
              than shown empty. The markup + SCSS (.c-expanded-cell-view__updated-by*)
              are intentionally KEPT so this can be restored verbatim once a
              follow-up backend ticket exposes the real author + timestamp — do NOT
              re-enable it with placeholder data. */}
            {/*
          <div
            className="c-expanded-cell-view__updated-by"
            test-id="ps-expanded-cell-view-updated-by"
          >
            <span>updated by</span>
            <span className="c-expanded-cell-view__updated-by-value">—</span>
          </div>
          */}
          </div>
        )
      ) : (
        <div
          className="c-expanded-cell-view__empty"
          test-id="ps-expanded-cell-view-empty"
        >
          {/* Cursor glyph illustration for the empty state (Figma design — the
              outlined pointer: tip top-left, tail to the bottom-right). Kept as a
              local inline SVG rather than a design-system icon: it is an
              app-specific empty-state decoration, not a shared UI glyph, and the
              design-system icon set has no cursor/pointer icon. */}
          <svg
            className="c-expanded-cell-view__empty-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
            <path d="M13 13l6 6" />
          </svg>
          {/* Typography is per the Figma spec (heading: Gordita Medium 18px/100%;
              subtitle: "Body/Body 3" — Gordita Regular 12px/20px). No design-system
              text variant matches those exact metrics, so these are plain elements
              styled with DS tokens + documented literals in the SCSS. */}
          <span className="c-expanded-cell-view__empty-title">
            Select one cell
          </span>
          <span className="c-expanded-cell-view__empty-subtitle">
            Select one cell to see an expanded view
          </span>
        </div>
      )}
    </ConfigPanel>
  );
});
